// Wires one replay onto the match stage: ReplayGameView fed by the playback clock, the shared matchView.ts
// render loop (as a spectator: no orders, the camera free or following a hero), fx.ts, the results screen at
// the end, and replayUi.ts's controls. The page-lifetime WebGL world is main.ts's; everything created here is
// undone by stop().
import * as THREE from 'three';
import { LAYOUT, type Team } from '../../core/layout.js';
import type { ReplayFile } from '../../shared/replay/format.js';
import type { SeatIndex } from '../../shared/protocol.js';
import { runMatchView } from '../matchView.js';
import { createFx } from '../fx.js';
import type { Sound } from '../sound.js';
import { t } from '../i18n/index.js';
import { teamName } from '../i18n/names.js';
import type { createEngraver } from '../engrave.js';
import type { buildMap } from '../mapView.js';
import { createPlayback } from './playback.js';
import { ReplayGameView, type Vision } from './replayView.js';
import { createReplayUi, type ReplayUiState } from './replayUi.js';
import { viewerSeatOf } from './replayList.js';
import { replayLink } from './replayLink.js';

const PAN_SPEED = 22;         // world units per second, WASD
const DRAG_UNITS_PER_PX = 0.05;

export type ReplayViewerDeps = {
  scene: THREE.Scene; camera: THREE.Camera; eng: ReturnType<typeof createEngraver>;
  stageEl: HTMLElement; hudEl: HTMLElement; canvas: HTMLCanvasElement; mapView: ReturnType<typeof buildMap>;
  params: URLSearchParams;
  /** Page-lifetime, like the online entry's: an AudioContext (and its unlock listeners) is not per viewer. */
  sound: Sound;
  file: ReplayFile;
  /** This browser's player name: finds their seat, for «Победа»/«Поражение» and the bold row. */
  viewerName: string | null;
  onExit(): void;
};

/** The hero the HUD hangs off: the viewer's own seat, else the first hero of the winning side (so the
    results screen reads «Победа» from the winners' side), else seat 0. */
export function anchorSeatOf(file: ReplayFile, viewerSeat: number | null): SeatIndex {
  if (viewerSeat !== null) return viewerSeat as SeatIndex;
  const team: Team = file.header.winner ?? 'a';
  return Math.max(0, file.header.roster.findIndex(r => r.team === team)) as SeatIndex;
}

/** The end banner for a recording that has no results screen (an aborted match, or one without stats). */
export function endBanner(file: ReplayFile): { title: string; sub: string } {
  const h = file.header;
  const score = `${teamName('a')} ${h.score.a} : ${h.score.b} ${teamName('b')}`;
  if (h.aborted) return { title: t('replay.aborted'), sub: score };
  return { title: t('replay.end'), sub: h.winner ? t('replay.endWinner', { score, team: teamName(h.winner) }) : score };
}

export function startReplayViewer(deps: ReplayViewerDeps) {
  const { scene, camera, eng, stageEl, hudEl, canvas, mapView, params, sound, file, viewerName, onExit } = deps;
  const { header, frames } = file;
  const viewerSeat = viewerSeatOf(header.roster, viewerName);
  const view = new ReplayGameView(file, anchorSeatOf(file, viewerSeat), viewerSeat);
  const playback = createPlayback(frames[0]!.world.tick, frames[frames.length - 1]!.world.tick, header.dt);
  const fx = createFx(scene, view, sound);

  // Camera: free by default, starting over the anchor's base; or glued to one hero.
  let follow: number | null = null;
  const [bx, bz] = LAYOUT.bases[view.player.team];
  const free = new THREE.Vector3(bx, 0, bz);
  const clampFree = () => {
    free.x = Math.max(-LAYOUT.bounds.x, Math.min(LAYOUT.bounds.x, free.x));
    free.z = Math.max(-LAYOUT.bounds.z, Math.min(LAYOUT.bounds.z, free.z));
  };
  /** Letting go of a followed hero leaves the camera where it was looking. */
  const unfollow = () => {
    if (follow === null) return;
    const h = view.heroes[follow]!;
    free.set(h.pos.x, 0, h.pos.z);
    follow = null;
  };
  const held = new Set<string>();
  const PAN_KEYS: Record<string, [number, number]> = { KeyW: [0, -1], KeyS: [0, 1], KeyA: [-1, 0], KeyD: [1, 0] };
  const onKeyDown = (e: KeyboardEvent) => { if (PAN_KEYS[e.code] && !e.ctrlKey && !e.metaKey) { held.add(e.code); unfollow(); } };
  const onKeyUp = (e: KeyboardEvent) => { held.delete(e.code); };
  const onBlur = () => held.clear();
  let drag: { x: number; y: number } | null = null;
  const onPointerDown = (e: PointerEvent) => { if (e.button === 0 || e.button === 2) { drag = { x: e.clientX, y: e.clientY }; canvas.setPointerCapture?.(e.pointerId); } };
  const onPointerMove = (e: PointerEvent) => {
    if (!drag) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) < 1) return;
    unfollow();
    free.x -= dx * DRAG_UNITS_PER_PX;
    free.z -= dy * DRAG_UNITS_PER_PX;
    clampFree();
    drag = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = () => { drag = null; };
  const onContextMenu = (e: Event) => e.preventDefault();
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('contextmenu', onContextMenu);
  function pan(dt: number) {
    let x = 0, z = 0;
    for (const k of held) { const d = PAN_KEYS[k]; if (d) { x += d[0]; z += d[1]; } }
    if (!x && !z) return;
    const k = PAN_SPEED * dt / Math.hypot(x, z);
    free.x += x * k; free.z += z * k;
    clampFree();
  }

  const setVision = (v: Vision) => { view.setVision(v); handle.invalidateVision(); };
  const restart = () => { playback.seek(playback.firstTick); playback.play(); };
  let stopped = false;
  function stop() {
    if (stopped) return;
    stopped = true;
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('contextmenu', onContextMenu);
    ui.destroy();
    fx.destroy();
    handle.stop();
    stageEl.classList.remove('replay-mode');
  }
  const exit = () => { stop(); onExit(); };

  stageEl.classList.add('replay-mode');
  const ui = createReplayUi(stageEl, header.roster, viewerSeat, {
    toggle: () => playback.toggle(),
    seekSec: sec => playback.seekSec(sec),
    seekBy: sec => playback.seekBy(sec),
    speed: s => playback.setSpeed(s),
    vision: setVision,
    hero: seat => { if (follow === seat) unfollow(); else follow = seat; },
    copyLink() {
      const link = replayLink(header.id);
      const done = () => ui.flash(t('common.linkCopied'));
      const fail = () => ui.flash(link);
      try { navigator.clipboard.writeText(link).then(done, fail); } catch { fail(); }
    },
    restart,
    exit,
  });
  const endState = endBanner(file);
  function uiState(): ReplayUiState {
    return {
      playing: playback.playing, speed: playback.speed, timeSec: playback.timeSec, totalSec: playback.totalSec,
      vision: view.vision, follow,
      heroes: view.heroes.map((h, i) => ({
        hp: h.hp, maxHp: h.maxHp, alive: h.alive, visible: h.visible,
        respawnIn: h.respawnAt > 0 ? h.respawnAt - view.t : null, kda: view.kda[i] ?? [0, 0, 0],
      })),
      end: playback.atEnd && view.winner === null ? endState : null,
    };
  }

  const handle = runMatchView({
    scene, camera, eng, stageEl, hudEl, canvas, mapView, sound, view, params,
    interactive: false,
    cameraTarget: () => (follow !== null ? view.heroes[follow]!.pos : free),
    stepSim(dt) {
      const played = playback.advance(dt);
      const jumped = playback.takeJump();
      view.update(playback.tick, played, jumped);
      if (jumped) handle.invalidateVision();
      pan(dt);
      return played;
    },
    afterStep(rootOf) {
      try { fx.update(rootOf); } finally { ui.update(uiState()); }
    },
    onBack: exit,
    backLabel: t('replay.exit'),
    resultsSecondary: () => ({ label: t('replay.again'), run: restart }),
  });

  return { view, playback, stop };
}
